
import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, query, orderByChild,equalTo, connectDatabaseEmulator } from "firebase/database";
import { initializeAuth, getAuth, GoogleAuthProvider, OAuthProvider, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, unlink, updatePhoneNumber, linkWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver, connectAuthEmulator } from "firebase/auth";
import { getStorage, ref as stRef, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getReactNativePersistence } from './react-native-persistance';

const FirebaseContext = createContext(null);

const isReactNativeRuntime = () => typeof document === 'undefined';

const shouldUseFirebaseEmulators = (config) => (
    (isReactNativeRuntime() && config && config.projectId === 'demo-garahex-e2e') ||
    (!isReactNativeRuntime() &&
        typeof process !== 'undefined' &&
        process.env &&
        process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true')
);

const isLocalWebHost = () => (
    typeof window !== 'undefined' &&
    window.location &&
    ['localhost', '127.0.0.1'].indexOf(window.location.hostname) !== -1
);

const emulatorConnectionState = {
    auth: false,
    database: false,
    functions: false,
    storage: false,
    infoLogged: false,
    warningLogged: false
};

const connectFirebaseEmulatorsIfEnabled = (auth, database, functionsInstance, storage, config) => {
    const useEmulators = shouldUseFirebaseEmulators(config);
    const devMode = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
    if (useEmulators) {
        if (auth && !emulatorConnectionState.auth) {
            connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
            emulatorConnectionState.auth = true;
        }
        if (database && !emulatorConnectionState.database) {
            connectDatabaseEmulator(database, '127.0.0.1', 9000);
            emulatorConnectionState.database = true;
        }
        if (functionsInstance && !emulatorConnectionState.functions) {
            connectFunctionsEmulator(functionsInstance, '127.0.0.1', 5001);
            emulatorConnectionState.functions = true;
        }
        if (storage && !emulatorConnectionState.storage) {
            connectStorageEmulator(storage, '127.0.0.1', 9199);
            emulatorConnectionState.storage = true;
        }
        if (devMode && !emulatorConnectionState.infoLogged) {
            console.log('Garahex is connected to Firebase Emulator Suite.');
            emulatorConnectionState.infoLogged = true;
        }
        return true;
    }
    if (devMode && isLocalWebHost() && !emulatorConnectionState.warningLogged) {
        console.warn('WARNING: Local Garahex is connected to non-emulated Firebase services.');
        emulatorConnectionState.warningLogged = true;
    }
    return false;
};


let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
    functions: null,
    useEmulators: false,
}   

const createFullStructure = (app, db, auth, storage, functionsInstance, config, useEmulators) => {
    return {
        app: app,
        config: config,
        database: db,
        auth: auth,
        storage: storage,
        functions: functionsInstance,
        useEmulators: !!useEmulators,
        functionsEmulatorOrigin: useEmulators ? 'http://127.0.0.1:5001' : '',
        functionUrl: (name) => useEmulators ? ('http://127.0.0.1:5001/' + config.projectId + '/us-central1/' + name) : ('https://us-central1-' + config.projectId + '.cloudfunctions.net/' + name),
        authRef:getAuth,
        googleProvider:new GoogleAuthProvider(),
        appleProvider:new OAuthProvider('apple.com'),
        phoneProvider:new PhoneAuthProvider(auth),          
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,   
        updatePhoneNumber:updatePhoneNumber,
        unlink: unlink,
        googleCredential: (idToken, accessToken)=> GoogleAuthProvider.credential(idToken, accessToken),    
        linkWithPhoneNumber: linkWithPhoneNumber,
        mobileAuthCredential: (verificationId,code) => PhoneAuthProvider.credential(verificationId, code),           
        usersRef: ref(db, 'users'),
        clientProfilesRef: ref(db, 'clientProfiles'),
        clientProfileRef:function(uid){return ref(db, 'clientProfiles/' + uid)},
        organizationClientsRef:function(organizationId){return ref(db, 'organizationClients/' + organizationId)},
        organizationClientRef:function(organizationId,relationshipId){return ref(db, 'organizationClients/' + organizationId + '/' + relationshipId)},
        clientMembershipsRef:function(clientAuthUid){return ref(db, 'clientMemberships/' + clientAuthUid)},
        clientMembershipRef:function(clientAuthUid,organizationId){return ref(db, 'clientMemberships/' + clientAuthUid + '/' + organizationId)},
        organizationOwnedClientsRef:function(organizationId){return ref(db, 'organizationOwned/' + organizationId + '/clients')},
        organizationOwnedBookingsRef:function(organizationId){return ref(db, 'organizationOwned/' + organizationId + '/bookings')},
        fleetAdminOwnedClientsRef:function(fleetAdminUid){return ref(db, 'fleetAdminOwned/' + fleetAdminUid + '/clients')},
        fleetAdminOwnedBookingsRef:function(fleetAdminUid){return ref(db, 'fleetAdminOwned/' + fleetAdminUid + '/bookings')},
        clientOwnedBookingsRef:function(clientAuthUid){return ref(db, 'clientOwned/' + clientAuthUid + '/bookings')},
        driverAssignedBookingsRef:function(driverUid){return ref(db, 'driverAssigned/' + driverUid + '/bookings')},
        clientRelationshipsRef:function(clientAuthUid){return ref(db, 'clientRelationships/' + clientAuthUid)},
        clientInvitationsRef: ref(db, 'clientInvitations'),
        rootRef: ref(db),
        bookingRef: ref(db, 'bookings'),
        bookingAccessRef:(bookingKey) => ref(db, 'bookingAccess/' + bookingKey),
        bookingOperationsRef:(bookingKey) => ref(db, 'bookingOperations/' + bookingKey),
        bookingPrivateRef:(bookingKey) => ref(db, 'bookingPrivate/' + bookingKey),
        bookingClientChargesRef:(bookingKey) => ref(db, 'bookingClientCharges/' + bookingKey),
        bookingFinancialsRef:(bookingKey) => ref(db, 'bookingFinancials/' + bookingKey),
        bookingMarketplaceRef:(bookingKey) => ref(db, 'bookingMarketplace/' + bookingKey),
        bookingMarketplaceListRef: ref(db, 'bookingMarketplace'),
        cancelreasonRef: ref(db, 'cancel_reason'),
        settingsRef: ref(db, 'settings'),
        smtpRef: ref(db, "smtpdata"),
        smsRef: ref(db, "smsConfig"),
        carTypesRef: ref(db, 'cartypes'),
        carTypesEditRef:(id) => ref(db, "cartypes/"+ id),
        carDocImage:(id) =>  stRef(storage, `cartypes/${id}`),     
        containerYardsRef: ref(db, 'container_yards'),
        containerYardEditRef:(id) => ref(db, "container_yards/" + id),
        additionalFeesRef: ref(db, 'additional_fees'),
        additionalFeeEditRef:(id) => ref(db, "additional_fees/" + id),
        workflowConfigurationsRef: ref(db, 'workflow_configurations'),
        workflowConfigurationEditRef:(id) => ref(db, "workflow_configurations/" + id),
        promoRef: ref(db, 'promos'),
        promoEditRef:(id) => ref(db, "promos/"+ id),
        notifyRef: ref(db,"notifications/"),
        notifyEditRef:(id) => ref(db, "notifications/"+ id),
        addressRef: (uid,id) =>  ref(db, "savedAddresses/"+ uid + "/" + id),
        addressEditRef:(uid) => ref(db, "savedAddresses/"+ uid),
        singleUserRef:(uid) => ref(db, "users/" + uid),
        profileImageRef:(uid) => stRef(storage,`users/${uid}/profileImage`),
        verifyIdImageRef:(uid) => stRef(storage,`users/${uid}/verifyIdImage`),
        bookingImageRef:(bookingId,imageType) => stRef(storage,`bookings/${bookingId}/${imageType}`),
        driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
        driverDocsRef:(uid) => stRef(storage,`users/${uid}/license`),       
        driverDocsRefBack:(uid) => stRef(storage,`users/${uid}/licenseBack`),         
        singleBookingRef:(bookingKey) => ref(db, "bookings/" + bookingKey),
        fleetOfferRef:(bookingKey, fleetAdminUid) => ref(db, "bookings/" + bookingKey + "/fleetOffers/" + fleetAdminUid),
        driverOfferRef:(bookingKey, driverUid) => ref(db, "bookings/" + bookingKey + "/driverOffers/" + driverUid),
        requestedDriversRef:(bookingKey ) => ref(db, "bookings/" + bookingKey  + "/requestedDrivers"),
        referralIdRef:(referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
        trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
        tasksRef:() => query(ref(db, 'bookingMarketplace'), orderByChild('status'), equalTo('NEW')),
        singleTaskRef:(uid,bookingId) => ref(db, "bookings/" + bookingId  + "/requestedDrivers/" + uid),
        bookingListRef:(uid,role) => 
            role == 'customer'? ref(db, 'clientOwned/' + uid + '/bookings'):
                (role == 'driver'? 
                    ref(db, 'driverAssigned/' + uid + '/bookings')
                    :
                    (role == 'fleetadmin'? 
                        ref(db, 'fleetAdminOwned/' + uid + '/bookings')
                        : ref(db, 'bookings')
                    )
                ),
        chatRef:(bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
        withdrawRef: ref(db, 'withdraws/'),
        languagesRef: ref(db, "languages"),
        languagesEditRef:(id) => ref(db, "languages/"+ id),
        walletHistoryRef:(uid) => ref(db, "walletHistory/" + uid),  
        userNotificationsRef:(uid) =>  ref(db, "userNotifications/"+ uid),
        userRatingsRef:(uid) =>  ref(db, "userRatings/"+ uid),
        carsRef:(uid,role) => role == 'driver'? 
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
            :(role == 'fleetadmin'? 
                query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                : ref(db, 'cars')
            ),
        carAddRef: ref(db, "cars"),
        carEditRef:(id) => ref(db, "cars/"+ id),
        carImage:(id) => stRef(storage,`cars/${id}`),   
        allLocationsRef: ref(db, "locations"),
        userLocationRef:(uid) => ref(db, "locations/"+ uid),
        sosRef: ref(db, 'sos'),
        editSosRef:(id) => ref(db, "sos/" +id),
        complainRef: ref(db, 'complain'),
        editComplainRef:(id) => ref(db, "complain/" +id),
        paymentSettingsRef: ref(db, "payment_settings"),
        usedreferralRef:ref(db,'usedreferral'),
    }
}

const FirebaseProvider  = ({ config, children, AsyncStorage, token }) => {
    let app, auth, database, storage, functionsInstance, useEmulators;

    if (!getApps().length) {
        try {
            app = initializeApp(config);

            if (typeof document !== 'undefined') {
                auth = initializeAuth(app, {
                    persistence: browserLocalPersistence,
                    popupRedirectResolver: browserPopupRedirectResolver,
                });
            }
            else{
                auth = initializeAuth(app, {
                    persistence: getReactNativePersistence(AsyncStorage),
                });
            }
            database = getDatabase(app);
            storage = getStorage(app);
            functionsInstance = getFunctions(app);
            useEmulators = connectFirebaseEmulatorsIfEnabled(auth, database, functionsInstance, storage, config);
        } catch (error) {
            console.log("Error initializing app: " + error);
        }
    } else {
        app = getApp();
        auth = getAuth(app);
        database = getDatabase(app);
        storage = getStorage(app);
        functionsInstance = getFunctions(app);
        useEmulators = connectFirebaseEmulatorsIfEnabled(auth, database, functionsInstance, storage, config);
    }

    firebase = createFullStructure(app, database, auth, storage, functionsInstance, config, useEmulators);

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    )
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
}
